import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
    Search,
    Filter,
    Check,
    X,
    User,
    Clock,
    AlertCircle,
    FileText,
    ChevronDown,
    DollarSign,
    CreditCard,
    Loader, Download, Eye, RotateCcw,
} from "lucide-react";
import Swal from "sweetalert2";
import { jsPDF } from "jspdf";
import latoRegularUrl from "../../../../fonts/Lato-Regular.ttf";
import latoBoldUrl from "../../../../fonts/Lato-Bold.ttf";
import loanRequestLogoUrl from "../../../../images/loan-request-logo.png";
import {
    getAdminAvanceInboxRequest,
    adminAvanceDecisionRequest,
    getAdminAvanceHistoryRequest,
    exportAvanceRequestsExcelRequest,
} from "../../../../redux/actions/avanceRequestActions";
import "../TraitementStyles/TraitementAvances.css";
import "../TraitementStyles/TreatmentMesOrdres.css";
import useTreatmentAutoRefresh from "./useTreatmentAutoRefresh";
import { confirmTreatmentApproval } from "./confirmTreatmentApproval";
import { getRequestTreatedBy } from "./treatmentDecisionInfo";

const SearchableCombobox = ({ value, onChange, options, placeholder }) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState(value || "");
    const wrapperRef = useRef(null);
    useEffect(() => { setQuery(value || ""); }, [value]);
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);
    const filteredOptions = useMemo(() => {
        if (!query) return options;
        const q = query.toLowerCase();
        return options.filter((o) => o.label.toLowerCase().includes(q));
    }, [options, query]);
    const handleSelect = (opt) => { setQuery(opt.label); onChange(opt.value); setOpen(false); };
    const handleInputChange = (e) => { setQuery(e.target.value); onChange(e.target.value); setOpen(true); };
    const handleClear = () => { setQuery(""); onChange(""); setOpen(false); };
    return (
        <div ref={wrapperRef} style={{ position: "relative", width: "100%" }}>
            <div style={{ position: "relative" }}>
                <input type="text" className="dcInput" style={{ width: "100%", fontSize: "13px", paddingRight: 56 }}
                       placeholder={placeholder} value={query} onChange={handleInputChange} onFocus={() => setOpen(true)} />
                <div style={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)", display: "flex", alignItems: "center", gap: 2 }}>
                    {query && (<button type="button" onClick={handleClear} style={{ border: "none", background: "none", cursor: "pointer", color: "#9ca3af", padding: 4, lineHeight: 0 }} title="Effacer"><X size={13} /></button>)}
                    <button type="button" onClick={() => setOpen(o => !o)} style={{ border: "none", background: "none", cursor: "pointer", color: "#9ca3af", padding: 4, lineHeight: 0 }} title="Afficher la liste"><ChevronDown size={14} /></button>
                </div>
            </div>
            {open && filteredOptions.length > 0 && (
                <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, boxShadow: "0 4px 12px rgba(0,0,0,0.10)", zIndex: 30, maxHeight: 220, overflowY: "auto" }}>
                    {filteredOptions.map((opt) => {
                        const isSelected = opt.value === value;
                        return (
                            <div key={opt.value} onClick={() => handleSelect(opt)}
                                 style={{ padding: "8px 12px", fontSize: 13, cursor: "pointer", background: isSelected ? "#eff6ff" : "#fff", color: isSelected ? "#204385" : "#374151", fontWeight: isSelected ? 600 : 400 }}
                                 onMouseEnter={(e) => (e.currentTarget.style.background = "#f8fafc")}
                                 onMouseLeave={(e) => (e.currentTarget.style.background = isSelected ? "#eff6ff" : "#fff")}>
                                {opt.label}
                            </div>
                        );
                    })}
                </div>
            )}
            {open && query && filteredOptions.length === 0 && (
                <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, boxShadow: "0 4px 12px rgba(0,0,0,0.10)", zIndex: 30, padding: "8px 12px", fontSize: 12, color: "#9ca3af" }}>
                    Aucun résultat
                </div>
            )}
        </div>
    );
};

const TraitementAvances = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    // Redux state
    const { adminInbox, loading } = useSelector((state) => state.avanceRequests);

    const [activeTab, setActiveTab] = useState("enCours");
    // Filtres
    const [filterCollab, setFilterCollab] = useState("");
    const [filterType, setFilterType] = useState("");
    const [filterDate, setFilterDate] = useState("");

    const handleResetFilters = () => {
        setFilterCollab("");
        setFilterType("");
        setFilterDate("");
    };

    const [showRejectModal, setShowRejectModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedDemande, setSelectedDemande] = useState(null);
    const [motifRejet, setMotifRejet] = useState("");

    // Charger les données au montage
    useEffect(() => {
        dispatch(getAdminAvanceInboxRequest());
        dispatch(getAdminAvanceHistoryRequest());
    }, [dispatch]);

    useTreatmentAutoRefresh(() => {
        dispatch(getAdminAvanceInboxRequest({ silent: true }));
        dispatch(getAdminAvanceHistoryRequest({ silent: true }));
    });

    const formatDate = (dateStr) => {
        if (!dateStr) return "N/A";
        const d = new Date(dateStr);
        return d.toLocaleDateString("fr-FR", {
            day: "2-digit",
            month: "short",
            year: "numeric"
        });
    };

    const formatMontant = (montant) => {
        if (!montant) return "0";
        return new Intl.NumberFormat("fr-MA", {
            style: "decimal",
            minimumFractionDigits: 0
        }).format(montant);
    };

    const nombreEnLettres = (value) => {
        const number = Math.max(0, Math.floor(Number(value) || 0));
        const units = ["zéro", "un", "deux", "trois", "quatre", "cinq", "six", "sept", "huit", "neuf", "dix", "onze", "douze", "treize", "quatorze", "quinze", "seize", "dix-sept", "dix-huit", "dix-neuf"];
        const tens = ["", "", "vingt", "trente", "quarante", "cinquante", "soixante"];
        if (number < 20) return units[number];
        if (number < 70) {
            const ten = Math.floor(number / 10);
            const rest = number % 10;
            return `${tens[ten]}${rest === 1 ? " et un" : rest ? `-${units[rest]}` : ""}`;
        }
        if (number < 80) return `soixante-${nombreEnLettres(number - 60)}`;
        if (number < 100) return `quatre-vingt${number === 80 ? "s" : `-${nombreEnLettres(number - 80)}`}`;
        if (number < 1000) {
            const hundred = Math.floor(number / 100);
            const rest = number % 100;
            return `${hundred === 1 ? "cent" : `${units[hundred]} cent`}${hundred > 1 && rest === 0 ? "s" : ""}${rest ? ` ${nombreEnLettres(rest)}` : ""}`;
        }
        if (number < 1000000) {
            const thousands = Math.floor(number / 1000);
            const rest = number % 1000;
            return `${thousands === 1 ? "mille" : `${nombreEnLettres(thousands)} mille`}${rest ? ` ${nombreEnLettres(rest)}` : ""}`;
        }
        if (number < 1000000000) {
            const millions = Math.floor(number / 1000000);
            const rest = number % 1000000;
            return `${nombreEnLettres(millions)} million${millions > 1 ? "s" : ""}${rest ? ` ${nombreEnLettres(rest)}` : ""}`;
        }
        return String(number);
    };

    // Génère le PDF de la demande de prêt en reproduisant fidèlement la mise
    // en page du modèle Word "Demande de prêt social sur salaire" :
    // cadre extérieur unique, champs alignés, puis 3 blocs d'avis encadrés
    // (Responsable Hiérarchique / Directeur RH côte à côte, puis Directeur
    // Général en pleine largeur) avec des cases "Favorable / Défavorable"
    // encadrées comme dans le document original.
    const handleDownloadLoanRequest = async (demande) => {
        try {
            const doc = new jsPDF({ unit: "mm", format: "a4" });
            let pdfFont = "helvetica";
            try {
                const toBase64 = (buffer) => {
                    const bytes = new Uint8Array(buffer);
                    let binary = "";
                    for (let i = 0; i < bytes.length; i += 0x8000) binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
                    return btoa(binary);
                };
                const [regular, bold] = await Promise.all([
                    fetch(latoRegularUrl).then((response) => response.arrayBuffer()),
                    fetch(latoBoldUrl).then((response) => response.arrayBuffer()),
                ]);
                doc.addFileToVFS("Lato-Regular.ttf", toBase64(regular));
                doc.addFileToVFS("Lato-Bold.ttf", toBase64(bold));
                doc.addFont("Lato-Regular.ttf", "Lato", "normal");
                doc.addFont("Lato-Bold.ttf", "Lato", "bold");
                pdfFont = "Lato";
            } catch (fontError) {
                console.warn("Police Lato indisponible pour le PDF, police de secours utilisée.", fontError);
            }

            const pageWidth = 210;
            const pageHeight = 297;
            // Le modèle Word encadre l'intégralité de la page d'un simple
            // trait : on reproduit ce cadre extérieur, puis on place le
            // texte avec une marge intérieure confortable.
            const outerMargin = 12;
            const margin = outerMargin + 8;
            const right = pageWidth - margin;

            const loanAmount = Number(demande.montantTotal || demande.montantDemande) || 0;
            const amount = `${formatMontant(loanAmount)} DH`;
            const monthlyAmount = `${formatMontant(demande.montantMensuel)} DH`;
            const months = Number(demande.nombreMensualites) || 0;
            // Le poste est fourni par l'API des demandes d'avances. Les autres
            // accès couvrent les données déjà chargées dans certains écrans.
            const employeeJobName = demande.employeeJobName
                || demande.employee?.job?.name
                || demande.jobName
                || demande.poste
                || demande.employeeFunction;

            const setText = (size = 11, bold = false) => {
                doc.setFont(pdfFont, bold ? "bold" : "normal");
                doc.setFontSize(size);
            };
            const text = (value, x, y, size = 11, bold = false, options = {}) => {
                setText(size, bold);
                doc.text(Array.isArray(value) ? value : String(value || "—"), x, y, options);
            };
            const underline = (x1, x2, yy) => {
                doc.setLineWidth(0.3);
                doc.line(x1, yy, x2, yy);
            };

            // ---- Cadre extérieur (comme dans le modèle Word) ----
            doc.setLineWidth(0.5);
            doc.rect(outerMargin, outerMargin, pageWidth - outerMargin * 2, pageHeight - outerMargin * 2);
            doc.setLineWidth(0.2);

            try {
                const logo = await new Promise((resolve, reject) => {
                    const image = new Image();
                    image.onload = () => resolve(image);
                    image.onerror = reject;
                    image.src = loanRequestLogoUrl;
                });
                doc.addImage(logo, "PNG", margin, outerMargin + 6, 22, 22);
            } catch (logoError) {
                console.warn("Logo indisponible pour la demande de prêt.", logoError);
            }

            // L'espace supérieur accueille le logo du modèle avant le titre.
            let y = outerMargin + 19;

            // En-tête société centré verticalement par rapport au logo.
            doc.setTextColor(42, 92, 169);
            text(demande.companyName || "SOCIÉTÉ", margin + 27, y, 12, true);
            doc.setTextColor(0, 0, 0);
            y += 22;

            // Titre centré et souligné
            const title = "DEMANDE DE PRÊT SOCIAL SUR SALAIRE";
            text(title, pageWidth / 2, y, 14, true, { align: "center" });
            const titleWidth = doc.getTextWidth(title);
            underline((pageWidth - titleWidth) / 2, (pageWidth + titleWidth) / 2, y + 1.6);
            y += 14;

            // Champs collaborateur, alignés comme "Nom & Prénom :", "Matricule :", "Fonction :"
            const valueX = margin + 36;
            const field = (label, value, yy) => {
                text(label, margin, yy, 11, false);
                text(`: ${value || "…………………………"}`, valueX, yy, 11, true);
            };
            field("Nom & Prénom", demande.employeeName, y); y += 6.5;
            field("Matricule", demande.employeeMatricule, y); y += 6.5;
            field("Fonction", employeeJobName, y); y += 6.5;

            const requestLine = doc.splitTextToSize(
                `Je demande un prêt de : ${amount} / ${nombreEnLettres(loanAmount)} dirhams.`,
                right - margin,
            );
            text(requestLine, margin, y, 11, false, { lineHeightFactor: 1.15 });
            y += requestLine.length * 4.5 + 5;

            const authorization = doc.splitTextToSize(
                `En conséquence, j'autorise par la présente ${demande.companyName || "la société"} à prélever chaque mois sur ma rémunération la somme de ${monthlyAmount} (${nombreEnLettres(demande.montantMensuel)} dirhams) jusqu'à remboursement du montant total du prêt.`,
                right - margin,
            );
            text(authorization, margin, y, 11, false, { lineHeightFactor: 1.15 });
            y += authorization.length * 4.5 + 5;

            const departure = doc.splitTextToSize(
                "Au cas où je viendrais à quitter la Société, pour quelque cause que ce soit, avant remboursement total du prêt, j'autorise cette dernière à retenir le solde sur mon compte de liquidation.",
                right - margin,
            );
            text(departure, margin, y, 11, false, { lineHeightFactor: 1.15 });
            y += departure.length * 4.5 + 5;

            // Bloc "NB :" + durée, comme dans le modèle
            text("NB :", margin, y, 11, true);
            underline(margin, margin + doc.getTextWidth("NB :"), y + 0.8);
            y += 6.5;
            const duration = doc.splitTextToSize(
                `La durée maximum du prêt est de ${nombreEnLettres(months)} (${months}) mois.`,
                right - margin,
            );
            text(duration, margin, y, 11); y += duration.length * 6 + 3;

            // Date à droite, puis "Signature :" centré et souligné (comme le modèle)
            const date = new Date().toLocaleDateString("fr-FR");
            text(`${demande.companyCity || "Ait Melloul"}, le : ${date}`, right, y, 10.5, false, { align: "right" });
            y += 8;
            const sigLabel = "Signature :";
            text(sigLabel, pageWidth / 2, y, 11, true, { align: "center" });
            const sigWidth = doc.getTextWidth(sigLabel);
            underline(pageWidth / 2 - sigWidth / 2, pageWidth / 2 + sigWidth / 2, y + 1.2);
            y += 16;

            // Bloc d'avis encadré générique
            const drawAvisBox = (x, top, w, h, title2, signatureAlign = "left") => {
                doc.rect(x, top, w, h);
                text(title2, x + w / 2, top + 7, 9, true, { align: "center" });
                const lineY = top + h - 7;
                if (signatureAlign === "right") {
                    text("Signature :", x + w - 5, lineY, 8.5, true, { align: "right" });
                } else {
                    text("Signature :", x + 5, lineY, 8.5, true);
                }
            };

            // Ligne du haut : Responsable Hiérarchique + Directeur RH, côte à côte
            const boxGap = 6;
            const topBoxWidth = (right - margin - boxGap) / 2;
            const topBoxHeight = 44;
            drawAvisBox(margin, y, topBoxWidth, topBoxHeight, "Avis du Responsable Hiérarchique");
            drawAvisBox(margin + topBoxWidth + boxGap, y, topBoxWidth, topBoxHeight, "Avis du Directeur RH");
            y += topBoxHeight + 8;

            // Bloc pleine largeur : Directeur Général
            const bottomBoxHeight = 38;
            drawAvisBox(margin, y, right - margin, bottomBoxHeight, "Avis du Directeur Général");

            doc.save(`Demande_de_pret_social_${demande.employeeMatricule || demande.id}.pdf`);
        } catch (error) {
            console.error("Erreur de génération de la demande de prêt", error);
            Swal.fire({ icon: "error", title: "Erreur", text: "La demande de prêt n'a pas pu être générée." });
        }
    };

    const handleDownloadLoanContract = async (demande) => {
        try {
            const doc = new jsPDF({ unit: "mm", format: "a4" });
            let pdfFont = "helvetica";
            try {
                const toBase64 = (buffer) => {
                    const bytes = new Uint8Array(buffer);
                    let binary = "";
                    for (let i = 0; i < bytes.length; i += 0x8000) binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
                    return btoa(binary);
                };
                const [regular, bold] = await Promise.all([
                    fetch(latoRegularUrl).then((response) => response.arrayBuffer()),
                    fetch(latoBoldUrl).then((response) => response.arrayBuffer()),
                ]);
                doc.addFileToVFS("Lato-Regular.ttf", toBase64(regular));
                doc.addFileToVFS("Lato-Bold.ttf", toBase64(bold));
                doc.addFont("Lato-Regular.ttf", "Lato", "normal");
                doc.addFont("Lato-Bold.ttf", "Lato", "bold");
                pdfFont = "Lato";
            } catch (fontError) {
                console.warn("Police Lato indisponible pour le PDF, police de secours utilisée.", fontError);
            }

            const pageWidth = 210;
            const margin = 20;
            const right = pageWidth - margin;
            const gender = String(demande.employeeGender || "").trim().toUpperCase();
            const civilite = gender === "F" ? "Mme" : gender === "H" ? "M." : "";
            const employee = [civilite, demande.employeeName || ""].filter(Boolean).join(" ");
            const loanAmount = Number(demande.montantTotal || demande.montantDemande) || 0;
            const totalAmount = new Intl.NumberFormat("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(loanAmount);
            const monthlyAmount = new Intl.NumberFormat("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(demande.montantMensuel) || 0);
            const toMonthYear = (value) => {
                if (!value) return "—";
                const date = new Date(`${String(value).slice(0, 10)}T12:00:00`);
                return Number.isNaN(date.getTime()) ? "—" : `${String(date.getMonth() + 1).padStart(2, "0")}/${date.getFullYear()}`;
            };
            const toFullDate = (value) => {
                const date = value ? new Date(`${String(value).slice(0, 10)}T12:00:00`) : new Date();
                return Number.isNaN(date.getTime()) ? "—" : date.toLocaleDateString("fr-FR");
            };
            const setText = (size = 11, bold = false) => {
                doc.setFont(pdfFont, bold ? "bold" : "normal");
                doc.setFontSize(size);
            };
            const text = (value, x, y, size = 11, bold = false, options = {}) => {
                setText(size, bold);
                doc.text(Array.isArray(value) ? value : String(value || ""), x, y, options);
            };
            const paragraph = (value, y) => {
                const lines = doc.splitTextToSize(value, right - margin);
                text(lines, margin, y, 9.5, false, { align: "justify", lineHeightFactor: 1.12, maxWidth: right - margin });
                return y + lines.length * 4.1;
            };

            let y = 20;
            text("CONTRAT DE PRET", pageWidth / 2, y, 14, true, { align: "center" });
            const titleWidth = doc.getTextWidth("CONTRAT DE PRET");
            doc.setLineWidth(0.3);
            doc.line((pageWidth - titleWidth) / 2, y + 1.4, (pageWidth + titleWidth) / 2, y + 1.4);
            y += 14;

            text("Entre les soussignés :", margin, y, 9.5, true); y += 8;
            text("La société Nouvelle Aveiro Maroc", margin, y, 9.5); y += 4.8;
            text("Dont le siège social est à Lot 633-635, Zone Industrielle à Ait Melloul,", margin, y, 9.5); y += 4.8;
            text("Représentée par M. Mohamed BICHA, en qualité de Directeur Général", margin, y, 9.5); y += 9;
            text("Et", margin, y, 9.5, true); y += 8;
            text(employee, margin, y, 9.5); y += 4.8;
            text(`Adresse : ${demande.employeeAddress || ""}`, margin, y, 9.5); y += 9;
            text("Il a été convenu et arrêté ce qui suit :", pageWidth / 2, y, 9.5, true, { align: "center" }); y += 10;

            text("Article 1 – Objet", margin, y, 9.5, true); y += 6;
            y = paragraph(`La société Nouvelle Aveiro Maroc accède à la demande de ${employee} de bénéficier d’un prêt lui permettant de faire face à un besoin immédiat en argent pour des raisons personnelles.`, y) + 4;
            y = paragraph(`En conséquence, la société Nouvelle Aveiro Maroc met ce jour à disposition de ${employee} qui reconnaît l’avoir reçue, la somme de ${totalAmount} dhs, et ce au titre d’un prêt consenti sans intérêt.`, y) + 6;

            text("Article 2 – Modalités de remboursement", margin, y, 9.5, true); y += 6;
            y = paragraph(`${employee} s’engage à rembourser ladite somme à la société par mensualités de ${monthlyAmount} dhs.`, y) + 4;
            y = paragraph(`${employee} autorise expressément la société à prélever sur son salaire ces mensualités à chaque échéance de la paie, et ce, à compter du salaire du mois ${toMonthYear(demande.dateDebutRemboursement)}.`, y) + 4;
            y = paragraph(`${employee} aura la faculté de se libérer de sa dette par anticipation, en totalité ou en fractions non inférieures à ${monthlyAmount} dhs. Les règlements effectués par anticipation s’imputeront sur les échéances restant à courir.`, y) + 4;
            y = paragraph(`En cas de résiliation du contrat de travail de ${employee} pour quelque motif que ce soit, avant extinction totale de sa dette, les sommes restant dues seront compensables avec les créances acquises par ${employee} contre la société et ${employee} s’engage à verser à la société le montant des sommes qu’il resterait à lui devoir.`, y) + 6;

            text(`Fait en deux exemplaires à Ait Melloul, le ${toFullDate(demande.adminDecisionAt || demande.createdAt)}`, margin, y, 9.5); y += 10;
            text("Signature :", margin, y, 9.5, true); y += 4.8;
            text("(Précédée de la mention manuscrite « Lu et Approuvé »).", margin, y, 9); y += 12;
            text(employee, margin, y, 9.5, true);
            text("Société Nouvelle Aveiro Maroc", right, y, 9.5, true, { align: "right" });

            doc.save(`Contrat_de_pret_${demande.employeeMatricule || demande.id}.pdf`);
        } catch (error) {
            console.error("Erreur de génération du contrat de prêt", error);
            Swal.fire({ icon: "error", title: "Erreur", text: "Le contrat de prêt n'a pas pu être généré." });
        }
    };

    const percentSalaire = (demande) => {
        // On suppose que le salaire est dans employeeSalary ou on peut l'estimer
        const salaire = demande.employeeSalary || 10000; // Valeur par défaut
        if (!salaire) return 0;
        return (demande.montantMensuel / salaire) * 100;
    };

    const getStatutBadge = (statut) => {
        const config = {
            PENDING_ADMIN: { label: "En attente", bg: "#fff3cd", color: "#856404" },
            APPROVED: { label: "Approuvé", bg: "#d4edda", color: "#155724" },
            REJECTED: { label: "Rejeté", bg: "#f8d7da", color: "#721c24" },
        };
        const c = config[statut] || config.PENDING_ADMIN;
        return (
            <span className="tavBadge" style={{ backgroundColor: c.bg, color: c.color }}>
                {c.label}
            </span>
        );
    };

    const handleApprove = (demandeId) => {
        confirmTreatmentApproval(() => dispatch(adminAvanceDecisionRequest({
            id: demandeId,
            decision: true,
            comment: ""
        })));
    };

    const handleReject = (demande) => {
        setSelectedDemande(demande);
        setShowRejectModal(true);
    };

    const confirmReject = () => {
        if (!motifRejet.trim() || !selectedDemande) return;

        dispatch(adminAvanceDecisionRequest({
            id: selectedDemande.id,
            decision: false,
            comment: motifRejet
        }));

        closeReject();
    };

    const showDetails = (demande) => {
        setSelectedDemande(demande);
        setShowDetailsModal(true);
    };

    const closeDetails = () => {
        setShowDetailsModal(false);
        setSelectedDemande(null);
    };

    const closeReject = () => {
        setShowRejectModal(false);
        setMotifRejet("");
        setSelectedDemande(null);
    };

    // Mapper les statuts Redux vers les onglets de l'interface
    const mapStatutToTab = (statut) => {
        if (statut === "PENDING_ADMIN") return "enCours";
        if (statut === "APPROVED") return "approuve";
        if (statut === "REJECTED") return "rejete";
        return "enCours";
    };

    const collaborateurOptions = useMemo(() => {
        if (!adminInbox) return [];
        const map = new Map();
        adminInbox.forEach((d) => {
            const name = d.employeeName || "";
            if (name && !map.has(name)) {
                map.set(name, { value: name, label: d.employeeMatricule ? `${name} (${d.employeeMatricule})` : name });
            }
        });
        return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
    }, [adminInbox]);

    const typeOptions = useMemo(() => {
        if (!adminInbox) return [];
        return [...new Set(adminInbox.map((d) => d.avanceType).filter(Boolean))]
            .sort().map((t) => ({ value: t, label: t }));
    }, [adminInbox]);

    const getFilteredDemandes = () => {
        if (!adminInbox || adminInbox.length === 0) return [];
        let filtered = adminInbox.filter((d) => mapStatutToTab(d.status) === activeTab);
        if (filterCollab) {
            const val = filterCollab.toLowerCase();
            filtered = filtered.filter((d) =>
                (d.employeeName || "").toLowerCase().includes(val) ||
                (d.employeeMatricule || "").toLowerCase().includes(val)
            );
        }
        if (filterType) {
            const val = filterType.toLowerCase();
            filtered = filtered.filter((d) => (d.avanceType || "").toLowerCase().includes(val));
        }
        if (filterDate) {
            filtered = filtered.filter((d) => {
                if (!d.createdAt) return false;
                return new Date(d.createdAt).toISOString().split('T')[0] === filterDate;
            });
        }
        return filtered;
    };

    const tabs = useMemo(() => {
        if (!adminInbox) return [
            { id: "enCours", label: "En cours", count: 0 },
            { id: "approuve", label: "Approuvées", count: 0 },
            { id: "rejete", label: "Rejetées", count: 0 },
        ];

        return [
            {
                id: "enCours",
                label: "En cours",
                count: adminInbox.filter((d) => d.status === "PENDING_ADMIN").length
            },
            {
                id: "approuve",
                label: "Approuvées",
                count: adminInbox.filter((d) => d.status === "APPROVED").length
            },
            {
                id: "rejete",
                label: "Rejetées",
                count: adminInbox.filter((d) => d.status === "REJECTED").length
            },
        ];
    }, [adminInbox]);



    const filtered = getFilteredDemandes();

    if (loading && (!adminInbox || adminInbox.length === 0)) {
        return (
            <div className="tavPage">
                <div className="tavWrap" style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
                    <Loader size={32} className="spin" />
                </div>
            </div>
        );
    }

    return (
        <div className="tavPage">
            <div className="tavWrap">
                {/* Header */}
                <div className="tavHeader">
                    <div>
                        <h1>{t("Traitement des demandes d'avances et prêts")}</h1>
                        <p>Gérez et validez les demandes d'avances sur salaire et prêts de vos collaborateurs</p>
                    </div>
                </div>

                {/* Filters Panel */}
                <div className="tavCard" style={{ overflow: "visible" }}>
                    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: "16px", padding: "4px" }}>
                        <div style={{ minWidth: 220, flex: "1 1 220px" }}>
                            <label style={{ fontSize: "12px", fontWeight: "600", color: "#64748b", display: "block", marginBottom: "4px" }}>Collaborateur / Matricule</label>
                            <SearchableCombobox value={filterCollab} onChange={setFilterCollab} options={collaborateurOptions} placeholder="Nom ou matricule..." />
                        </div>
                        <div style={{ minWidth: 200, flex: "1 1 200px" }}>
                            <label style={{ fontSize: "12px", fontWeight: "600", color: "#64748b", display: "block", marginBottom: "4px" }}>Type d'avance</label>
                            <SearchableCombobox value={filterType} onChange={setFilterType} options={typeOptions} placeholder="Rechercher un type..." />
                        </div>
                        <div style={{ minWidth: 160, flex: "1 1 160px" }}>
                            <label style={{ fontSize: "12px", fontWeight: "600", color: "#64748b", display: "block", marginBottom: "4px" }}>Date de demande</label>
                            <input type="date" value={filterDate} onChange={(e) => setFilterDate(e.target.value)} className="dcInput" style={{ width: "100%", fontSize: "13px" }} />
                        </div>
                        <div style={{ display: "flex", gap: 8, flexShrink: 0, alignItems: "flex-end" }}>
                            <button type="button" onClick={handleResetFilters} className="dcBtn dcBtn--outline" style={{ padding: "10px 12px", display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", height: "38px", whiteSpace: "nowrap" }}>
                                <RotateCcw size={14} /> Réinitialiser
                            </button>
                            <button type="button" className="dcBtn dcBtn--outline" style={{ padding: "10px 14px", display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", height: "38px", background: "#10b981", color: "white", border: "none", whiteSpace: "nowrap" }}
                                    onClick={() => dispatch(exportAvanceRequestsExcelRequest("admin_inbox"))}>
                                <Download size={14} /> Exporter Excel
                            </button>
                        </div>
                    </div>
                </div>

                {/* Tabs */}
                <div className="tavCard tavCard--noPad">
                    <div className="tavTabs">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                type="button"
                                onClick={() => setActiveTab(tab.id)}
                                className={`tavTab ${activeTab === tab.id ? "is-active" : ""}`}
                            >
                                {tab.label}
                                <span className={`tavCount ${activeTab === tab.id ? "is-active" : ""}`}>
                                    {tab.count}
                                </span>
                            </button>
                        ))}
                    </div>

                    {/* Table */}
                    <div className="tavTableWrap">
                        {filtered.length === 0 ? (
                            <div className="tavEmpty">
                                <AlertCircle size={46} />
                                <div>Aucune demande trouvée</div>
                            </div>
                        ) : (
                            <table className="tavTable">
                                <thead>
                                <tr>
                                    <th>Matricule</th>
                                    <th>Collaborateur</th>
                                    <th>Type</th>
                                    <th>Montant demandé</th>
                                    <th>Remboursement</th>
                                    <th>Date demande</th>
                                    {(activeTab === "approuve" || activeTab === "rejete") && <th>Traitée par</th>}
                                    {activeTab === "rejete" && <th>Motif</th>}
                                    <th className="is-right">Actions</th>
                                </tr>
                                </thead>
                                <tbody>
                                {filtered.map((demande) => (
                                    <tr
                                        key={demande.id}
                                        className={demande.urgent ? "tavRowUrgent" : ""}
                                        onClick={() => showDetails(demande)}
                                        title={demande.urgent ? "Demande urgente" : ""}
                                    >
                                        <td>
                                            <span className="tavPrimary">{demande.employeeMatricule || "N/A"}</span>
                                        </td>

                                        <td>
                                            <div className="tavName">
                                                <span className="tavPrimary">{demande.employeeName}</span>
                                                {demande.urgent && <span className="tavFire">🔥</span>}
                                            </div>
                                        </td>

                                        <td>
                                            <div className="tavType">
                                                <span className="tavDot" style={{ backgroundColor: "#3b82f6" }} />
                                                <div>
                                                    <div className="tavPrimary">{demande.avanceType}</div>
                                                </div>
                                            </div>
                                        </td>

                                        <td>
                                            <div className="tavAmount">
                                                <div className="tavAmountMain">{formatMontant(demande.montantDemande)} dhs</div>
                                                {demande.tauxInteret > 0 && (
                                                    <div className="tavSub">Taux: {demande.tauxInteret}%</div>
                                                )}
                                            </div>
                                        </td>

                                        <td>
                                            <div className="tavPrimary">{demande.nombreMensualites} mois</div>
                                            <div className="tavSub">{formatMontant(demande.montantMensuel)} dhs/mois</div>
                                        </td>

                                        <td>
                                            <span className="tavMuted">{formatDate(demande.createdAt)}</span>
                                        </td>

                                        {(activeTab === "approuve" || activeTab === "rejete") && (
                                            <td><span className="tavMuted">{getRequestTreatedBy(demande)}</span></td>
                                        )}

                                        {activeTab === "rejete" && (
                                            <td>
                                                <span className="tavMuted" style={{ maxWidth: 160, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                    {demande.adminComment || demande.managerComment || "—"}
                                                </span>
                                            </td>
                                        )}

                                        <td className="is-right" onClick={(e) => e.stopPropagation()}>
                                            <div className="tavRowActions">
                                                {/(prêt|pret)/i.test(demande.avanceType || "") && demande.status !== "PENDING_ADMIN" && (
                                                    <button
                                                        type="button"
                                                        className="tavActionBtn"
                                                        onClick={() => handleDownloadLoanRequest(demande)}
                                                        title="Télécharger la demande de prêt"
                                                        aria-label="Télécharger la demande de prêt"
                                                        style={{ background: "#ecfdf5", color: "#059669", border: "1px solid #a7f3d0" }}
                                                    >
                                                        <Download size={16} />
                                                    </button>
                                                )}
                                                {/(prêt|pret)/i.test(demande.avanceType || "") && demande.status === "APPROVED" && (
                                                    <button
                                                        type="button"
                                                        className="tavActionBtn"
                                                        onClick={() => handleDownloadLoanContract(demande)}
                                                        title="Télécharger le contrat de prêt"
                                                        aria-label="Télécharger le contrat de prêt"
                                                        style={{ background: "#eff6ff", color: "#2563eb", border: "1px solid #bfdbfe" }}
                                                    >
                                                        <FileText size={16} />
                                                    </button>
                                                )}
                                                {activeTab === "enCours" && (
                                                    <>
                                                        <button
                                                            type="button"
                                                            className="tavActionBtn tavActionBtn--ok"
                                                            onClick={() => handleApprove(demande.id)}
                                                            title="Approuver"
                                                            disabled={loading}
                                                        >
                                                            <Check size={16} />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="tavActionBtn tavActionBtn--no"
                                                            onClick={() => handleReject(demande)}
                                                            title="Refuser"
                                                            disabled={loading}
                                                        >
                                                            <X size={16} />
                                                        </button>
                                                    </>
                                                )}
                                                <button
                                                    type="button"
                                                    className="tavActionBtn"
                                                    onClick={() => showDetails(demande)}
                                                    title="Voir les détails"
                                                    style={{ background: "#eff6ff", color: "#3b82f6", border: "1px solid #bfdbfe" }}
                                                >
                                                    <Eye size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>

            {/* DETAILS MODAL */}
            {showDetailsModal && selectedDemande && (
                <div className="tavModal">
                    <div className="tavModal__panel" role="dialog" aria-modal="true">
                        <div className="tavModal__header">
                            <div className="tavModal__headLeft">
                                <div className="tavModal__icon">
                                    <CreditCard size={18} />
                                </div>
                                <div>
                                    <div className="tavModal__title">Détails de la demande #{selectedDemande.id}</div>
                                    <div className="tavModal__sub">Demande du {formatDate(selectedDemande.createdAt)}</div>
                                </div>
                            </div>

                            <button type="button" className="tavIconBtn" onClick={closeDetails} aria-label="Fermer">
                                <X size={18} />
                            </button>
                        </div>

                        <div className="tavModal__body">
                            <div className="tavGrid2">
                                {/* Infos collaborateur */}
                                <div className="tavPanel">
                                    <div className="tavPanel__title">
                                        <User size={16} /> Informations collaborateur
                                    </div>

                                    <div className="tavKv">
                                        <span>Matricule</span>
                                        <b>{selectedDemande.employeeMatricule || "N/A"}</b>
                                    </div>
                                    <div className="tavKv">
                                        <span>Nom</span>
                                        <b>{selectedDemande.employeeName}</b>
                                    </div>
                                </div>

                                {/* Détails demande */}
                                <div className="tavPanel">
                                    <div className="tavPanel__title">
                                        <CreditCard size={16} /> Détails de la demande
                                    </div>

                                    <div className="tavKv">
                                        <span>Type</span>
                                        <b>{selectedDemande.avanceType}</b>
                                    </div>
                                    <div className="tavKv">
                                        <span>Montant demandé</span>
                                        <b className="tavAccent">{formatMontant(selectedDemande.montantDemande)} dhs</b>
                                    </div>
                                    <div className="tavKv">
                                        <span>Mensualités</span>
                                        <b>{selectedDemande.nombreMensualites} mois</b>
                                    </div>
                                    <div className="tavKv">
                                        <span>Taux d'intérêt</span>
                                        <b>{selectedDemande.tauxInteret || 0}%</b>
                                    </div>
                                    <div className="tavKv">
                                        <span>Mensualité</span>
                                        <b>{formatMontant(selectedDemande.montantMensuel)} dhs</b>
                                    </div>
                                    <div className="tavKv">
                                        <span>Total à rembourser</span>
                                        <b>{formatMontant(selectedDemande.montantTotal)} dhs</b>
                                    </div>
                                </div>

                                {/* Motif */}
                                <div className="tavPanel tavPanel--full">
                                    <div className="tavPanel__title">
                                        <FileText size={16} /> Motif de la demande
                                    </div>
                                    <div className="tavText">{selectedDemande.motif || " - "}</div>
                                </div>

                                {/* Justificatifs */}
                                {selectedDemande.justificatifs && selectedDemande.justificatifs.length > 0 && (
                                    <div className="tavPanel">
                                        <div className="tavPanel__title">
                                            <FileText size={16} /> Justificatifs fournis
                                        </div>
                                        <div className="tavTags">
                                            {selectedDemande.justificatifs.map((doc, idx) => (
                                                <span key={idx} className="tavTag">
                                                    {doc.split('/').pop()}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Décision manager (si applicable) */}
                                {selectedDemande.managerDecision !== null && (
                                    <div className="tavPanel tavPanel--full">
                                        <div className="tavPanel__title">
                                            <Check size={16} /> Validation Manager
                                        </div>
                                        <div className="tavKv">
                                            <span>Décision</span>
                                            <b style={{ color: selectedDemande.managerDecision ? "#10b981" : "#ef4444" }}>
                                                {selectedDemande.managerDecision ? "Approuvé" : "Rejeté"}
                                            </b>
                                        </div>
                                        {selectedDemande.managerName && (
                                            <div className="tavKv">
                                                <span>Par</span>
                                                <b>{selectedDemande.managerName}</b>
                                            </div>
                                        )}
                                        {selectedDemande.managerDecisionAt && (
                                            <div className="tavKv">
                                                <span>Date</span>
                                                <b>{formatDate(selectedDemande.managerDecisionAt)}</b>
                                            </div>
                                        )}
                                        {selectedDemande.managerComment && (
                                            <div className="tavRejectBox">
                                                <div className="tavRejectLabel">Commentaire</div>
                                                <div className="tavRejectText">{selectedDemande.managerComment}</div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Validation RH (si déjà traitée) */}
                                {selectedDemande.status !== "PENDING_ADMIN" && selectedDemande.adminDecision !== null && (
                                    <div className={`tavPanel tavPanel--full ${selectedDemande.status === "APPROVED" ? "tavValidOk" : "tavValidNo"}`}>
                                        <div className="tavPanel__title">
                                            {selectedDemande.status === "APPROVED" ? <Check size={16} /> : <X size={16} />}
                                            {selectedDemande.status === "APPROVED" ? " Demande approuvée" : " Demande rejetée"}
                                        </div>

                                        <div className="tavKv">
                                            <span>Date de validation</span>
                                            <b>{formatDate(selectedDemande.adminDecisionAt)}</b>
                                        </div>
                                        {selectedDemande.adminName && (
                                            <div className="tavKv">
                                                <span>Validé par</span>
                                                <b>{selectedDemande.adminName}</b>
                                            </div>
                                        )}

                                        {selectedDemande.adminComment && (
                                            <div className="tavRejectBox">
                                                <div className="tavRejectLabel">
                                                    {selectedDemande.status === "APPROVED" ? "Commentaire" : "Motif du rejet"}
                                                </div>
                                                <div className="tavRejectText">{selectedDemande.adminComment}</div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Progression du remboursement */}
                                {selectedDemande.status === "APPROVED" && selectedDemande.statutRemboursement !== null && (
                                    <>
                                        <div className="tavPanel tavPanel--full">
                                            <div className="tavPanel__title">
                                                <Check size={16} /> Progression du remboursement
                                            </div>
                                            <div className="tavMiniProgressTop">
                                                <span>
                                                    Payées : {selectedDemande.mensualitesPayees}/{selectedDemande.nombreMensualites}
                                                </span>
                                                <span className="tavAccent" style={{color: "#ef4444"}}>
                                                    Restant : {formatMontant(selectedDemande.montantTotal - (selectedDemande.montantRembourse || 0))} dhs
                                                </span>
                                            </div>
                                            <div className="tavProgressBar">
                                                <div
                                                    className="tavProgressFill"
                                                    style={{
                                                        width: `${(selectedDemande.mensualitesPayees / selectedDemande.nombreMensualites) * 100}%`
                                                    }}
                                                />
                                            </div>
                                            {selectedDemande.dateDebutRemboursement && (
                                                <div className="tavHelp">
                                                    Début remboursement : {formatDate(selectedDemande.dateDebutRemboursement)}
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Actions footer */}
                        <div className="tavModal__footer">
                            {/(prêt|pret)/i.test(selectedDemande.avanceType || "") && selectedDemande.status !== "PENDING_ADMIN" && (
                                <button
                                    type="button"
                                    className="tavBtn tavBtn--ghost"
                                    onClick={() => handleDownloadLoanRequest(selectedDemande)}
                                >
                                    <Download size={16} /> Télécharger la demande de prêt
                                </button>
                            )}
                            {/(prêt|pret)/i.test(selectedDemande.avanceType || "") && selectedDemande.status === "APPROVED" && (
                                <button
                                    type="button"
                                    className="tavBtn tavBtn--ghost"
                                    onClick={() => handleDownloadLoanContract(selectedDemande)}
                                >
                                    <FileText size={16} /> Télécharger le contrat de prêt
                                </button>
                            )}
                            {selectedDemande.status === "PENDING_ADMIN" ? (
                                <>
                                    <button
                                        type="button"
                                        className="tavBtn tavBtn--danger"
                                        onClick={() => {
                                            closeDetails();
                                            handleReject(selectedDemande);
                                        }}
                                        disabled={loading}
                                    >
                                        Refuser
                                    </button>

                                    <button
                                        type="button"
                                        className="tavBtn tavBtn--ok"
                                        onClick={() => {
                                            handleApprove(selectedDemande.id);
                                            closeDetails();
                                        }}
                                        disabled={loading}
                                    >
                                        {loading ? <Loader size={16} className="spin" /> : "Approuver"}
                                    </button>

                                    <button type="button" className="tavBtn tavBtn--ghost" onClick={closeDetails}>
                                        Fermer
                                    </button>
                                </>
                            ) : (
                                <button type="button" className="tavBtn tavBtn--ghost" onClick={closeDetails}>
                                    Fermer
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* REJECT MODAL */}
            {showRejectModal && selectedDemande && (
                <div className="tavModal">
                    <div className="tavModal__panel tavModal__panel--small" role="dialog" aria-modal="true">
                        <div className="tavModal__header">
                            <div className="tavModal__headLeft">
                                <div className="tavModal__icon tavModal__icon--danger">
                                    <AlertCircle size={18} />
                                </div>
                                <div>
                                    <div className="tavModal__title">Refuser la demande d'avance/prêt</div>
                                    <div className="tavModal__sub">
                                        {selectedDemande.employeeName} — {selectedDemande.avanceType}
                                    </div>
                                </div>
                            </div>

                            <button type="button" className="tavIconBtn" onClick={closeReject} aria-label="Fermer">
                                <X size={18} />
                            </button>
                        </div>

                        <div className="tavModal__body">
                            <div className="tavMiniCard">
                                <div className="tavMiniRow">
                                    <span className="tavMuted">Montant demandé</span>
                                    <b className="tavAccent">{formatMontant(selectedDemande.montantDemande)} dhs</b>
                                </div>
                                <div className="tavMiniRow">
                                    <span className="tavMuted">Remboursement</span>
                                    <b>
                                        {selectedDemande.nombreMensualites} mois × {formatMontant(selectedDemande.montantMensuel)} dhs
                                    </b>
                                </div>
                            </div>

                            <label className="tavLabel">
                                Motif du rejet <span className="tavReq">*</span>
                            </label>
                            <textarea
                                value={motifRejet}
                                required
                                onChange={(e) => setMotifRejet(e.target.value)}
                                rows={5}
                                placeholder="Veuillez préciser le motif du rejet de cette demande..."
                                className="tavTextarea"
                            />
                            <div className="tavHelp">Ce motif sera communiqué au collaborateur</div>
                        </div>

                        <div className="tavModal__footer">
                            <button type="button" className="tavBtn tavBtn--ghost" onClick={closeReject}>
                                Annuler
                            </button>

                            <button
                                type="button"
                                className="tavBtn tavBtn--danger"
                                onClick={confirmReject}
                                disabled={!motifRejet.trim() || loading}
                            >
                                {loading ? <Loader size={16} className="spin" /> : "Confirmer le rejet"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TraitementAvances;
